/**
 * MDJ Ritual — Interactive Sliding Puzzle
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-input.js
 *
 * Translates raw pointer/mouse/touch/keyboard events into calls against
 * the MDJRitual.Board public API. This file owns no puzzle state — it
 * only ever asks the board "is this position adjacent to empty?" and
 * "move this position", and otherwise manipulates transient visual state
 * (the mdj-tile--lifted / --dragging classes, and a live drag transform)
 * that the board's own layout pass will overwrite on the next move.
 *
 * Interaction model:
 *   - Tap (pointerdown + pointerup with negligible movement) on a movable
 *     tile commits an immediate move.
 *   - Drag/swipe on a movable tile follows the pointer along the single
 *     axis toward the empty cell, clamped to the empty cell's distance.
 *     Releasing past a distance or velocity threshold commits the move;
 *     otherwise the tile springs back.
 *   - Arrow keys move the tile that would slide in that direction,
 *     regardless of which element currently has focus, as long as focus
 *     is somewhere inside the board.
 *   - Enter/Space on a focused tile moves that specific tile if movable.
 *
 * Public API consumed by mdj-core.js:
 *   destroy()
 */

(function () {
  'use strict';

  window.MDJRitual = window.MDJRitual || {};

  var DRAG_COMMIT_FRACTION = 0.35; // fraction of cell size to commit a move
  var SWIPE_VELOCITY_THRESHOLD = 0.55; // px/ms — fast flicks commit early
  var TAP_MOVEMENT_TOLERANCE = 6; // px

  var KEY_DIRECTIONS = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    Up: 'up', // legacy IE/Edge key names
    Down: 'down',
    Left: 'left',
    Right: 'right'
  };

  /**
   * @param {HTMLElement} boardEl - the [data-mdj-board] container.
   * @param {Object} board - an MDJRitual.Board instance.
   * @param {Object} options
   * @param {boolean} options.reducedMotion
   */
  function MDJInput(boardEl, board, options) {
    this.boardEl = boardEl;
    this.board = board;
    this.options = options || {};

    this.dragState = null;
    this.usingPointerEvents = 'PointerEvent' in window;

    // Prevent the browser from treating drags inside the board as page
    // scroll/pan gestures on touch devices.
    this.boardEl.style.touchAction = 'none';

    this._bindHandlers();
    this._attachListeners();
  }

  /* ------------------------------------------------------------------ */
  /* Binding                                                              */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._bindHandlers = function () {
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  };

  MDJInput.prototype._attachListeners = function () {
    if (this.usingPointerEvents) {
      this.boardEl.addEventListener('pointerdown', this._onPointerDown);
      // move/up are bound to the window, not the board, so a drag that
      // leaves the board bounds is still tracked and resolved cleanly.
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    } else {
      this.boardEl.addEventListener('touchstart', this._onTouchStart, { passive: false });
      window.addEventListener('touchmove', this._onTouchMove, { passive: false });
      window.addEventListener('touchend', this._onTouchEnd);
      window.addEventListener('touchcancel', this._onTouchEnd);

      this.boardEl.addEventListener('mousedown', this._onMouseDown);
      window.addEventListener('mousemove', this._onMouseMove);
      window.addEventListener('mouseup', this._onMouseUp);
    }

    this.boardEl.addEventListener('keydown', this._onKeyDown);
  };

  /* ------------------------------------------------------------------ */
  /* Shared drag lifecycle (used by both Pointer Events and the fallback) */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._resolveTileFromTarget = function (target) {
    while (target && target !== this.boardEl) {
      if (target.classList && target.classList.contains('mdj-tile')) return target;
      target = target.parentNode;
    }
    return null;
  };

  MDJInput.prototype._beginInteraction = function (clientX, clientY, targetEl, pointerId) {
    var tileEl = this._resolveTileFromTarget(targetEl);
    if (!tileEl || !tileEl.classList.contains('mdj-tile--movable')) return;

    var position = this.board.getPositionOfElement(tileEl);
    if (position === -1 || !this.board.isAdjacentToEmpty(position)) return;

    var emptyPosition = this.board.getEmptyPosition();
    var metrics = this.board.getCellMetrics();
    var step = metrics.cellSize + metrics.gap;

    var axis, sign;
    var diff = emptyPosition - position;

    if (diff === metrics.gridSize) {
      axis = 'y';
      sign = 1; // empty is below -> tile moves down
    } else if (diff === -metrics.gridSize) {
      axis = 'y';
      sign = -1; // empty is above -> tile moves up
    } else if (diff === 1) {
      axis = 'x';
      sign = 1; // empty is to the right -> tile moves right
    } else {
      axis = 'x';
      sign = -1; // empty is to the left -> tile moves left
    }

    var baseTransform = tileEl.style.transform;

    this.dragState = {
      tileEl: tileEl,
      position: position,
      pointerId: pointerId,
      axis: axis,
      sign: sign,
      step: step,
      startX: clientX,
      startY: clientY,
      startTime: performance.now(),
      lastX: clientX,
      lastY: clientY,
      lastTime: performance.now(),
      velocity: 0,
      baseTransform: baseTransform,
      moved: false
    };

    tileEl.classList.add('mdj-tile--lifted');
  };

  MDJInput.prototype._updateInteraction = function (clientX, clientY) {
    var state = this.dragState;
    if (!state) return;

    var deltaX = clientX - state.startX;
    var deltaY = clientY - state.startY;
    var rawDelta = state.axis === 'x' ? deltaX : deltaY;

    // Only allow motion in the direction toward the empty cell; clamp
    // backward motion to zero so the tile cannot appear to leave its slot
    // in the wrong direction.
    var directional = rawDelta * state.sign;
    var clamped = Math.max(0, Math.min(state.step, directional)) * state.sign;

    if (!state.moved && Math.abs(deltaX) + Math.abs(deltaY) > TAP_MOVEMENT_TOLERANCE) {
      state.moved = true;
      state.tileEl.classList.add('mdj-tile--dragging');
    }

    if (state.moved) {
      var translateX = state.axis === 'x' ? clamped : 0;
      var translateY = state.axis === 'y' ? clamped : 0;
      state.tileEl.style.transform =
        state.baseTransform + ' translate3d(' + translateX + 'px, ' + translateY + 'px, 0)';
    }

    var now = performance.now();
    var dt = now - state.lastTime;
    if (dt > 0) {
      var dLast = (state.axis === 'x' ? clientX - state.lastX : clientY - state.lastY) * state.sign;
      state.velocity = dLast / dt;
    }
    state.lastX = clientX;
    state.lastY = clientY;
    state.lastTime = now;
  };

  MDJInput.prototype._endInteraction = function () {
    var state = this.dragState;
    if (!state) return;

    state.tileEl.classList.remove('mdj-tile--dragging');
    state.tileEl.classList.remove('mdj-tile--lifted');

    if (!state.moved) {
      // Treated as a tap: commit unconditionally since we already
      // verified the tile is movable at interaction start.
      this.board.moveTileAtPosition(state.position);
      this.dragState = null;
      return;
    }

    var deltaX = state.lastX - state.startX;
    var deltaY = state.lastY - state.startY;
    var rawDelta = state.axis === 'x' ? deltaX : deltaY;
    var directional = rawDelta * state.sign;
    var traveledFraction = Math.max(0, Math.min(1, directional / state.step));

    var shouldCommit =
      traveledFraction >= DRAG_COMMIT_FRACTION || state.velocity >= SWIPE_VELOCITY_THRESHOLD;

    if (shouldCommit) {
      // Reset the inline transform override before asking the board to
      // move — the board's own _placeTile call will set the correct
      // final transform and animate to it.
      state.tileEl.style.transform = state.baseTransform;
      this.board.moveTileAtPosition(state.position);
    } else {
      // Spring back to the original spot. Re-enable the CSS transition
      // (removed the dragging class above) so this animates smoothly.
      state.tileEl.style.transform = state.baseTransform;
    }

    this.dragState = null;
  };

  /* ------------------------------------------------------------------ */
  /* Pointer Events path (covers mouse, touch, and pen in one API)        */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._onPointerDown = function (event) {
    if (event.button !== undefined && event.button !== 0) return; // left click / primary only
    this._beginInteraction(event.clientX, event.clientY, event.target, event.pointerId);
    if (this.dragState) {
      event.preventDefault();
    }
  };

  MDJInput.prototype._onPointerMove = function (event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    event.preventDefault();
    this._updateInteraction(event.clientX, event.clientY);
  };

  MDJInput.prototype._onPointerUp = function (event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    this._endInteraction();
  };

  /* ------------------------------------------------------------------ */
  /* Touch fallback (browsers without Pointer Events)                     */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._onTouchStart = function (event) {
    if (this.dragState) return; // one active interaction at a time
    var touch = event.changedTouches[0];
    this._beginInteraction(touch.clientX, touch.clientY, event.target, touch.identifier);
    if (this.dragState) event.preventDefault();
  };

  MDJInput.prototype._onTouchMove = function (event) {
    if (!this.dragState) return;
    var touch = this._findTouch(event.changedTouches, this.dragState.pointerId);
    if (!touch) return;
    event.preventDefault();
    this._updateInteraction(touch.clientX, touch.clientY);
  };

  MDJInput.prototype._onTouchEnd = function (event) {
    if (!this.dragState) return;
    var touch = this._findTouch(event.changedTouches, this.dragState.pointerId);
    if (!touch) return;
    this._endInteraction();
  };

  MDJInput.prototype._findTouch = function (touchList, identifier) {
    for (var i = 0; i < touchList.length; i++) {
      if (touchList[i].identifier === identifier) return touchList[i];
    }
    return null;
  };

  /* ------------------------------------------------------------------ */
  /* Mouse fallback (browsers without Pointer Events)                      */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._onMouseDown = function (event) {
    if (event.button !== 0) return;
    this._beginInteraction(event.clientX, event.clientY, event.target, 'mouse');
    if (this.dragState) event.preventDefault();
  };

  MDJInput.prototype._onMouseMove = function (event) {
    if (!this.dragState) return;
    this._updateInteraction(event.clientX, event.clientY);
  };

  MDJInput.prototype._onMouseUp = function () {
    if (!this.dragState) return;
    this._endInteraction();
  };

  /* ------------------------------------------------------------------ */
  /* Keyboard                                                             */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype._onKeyDown = function (event) {
    var direction = KEY_DIRECTIONS[event.key];

    if (direction) {
      event.preventDefault();
      this.board.trySlideDirection(direction);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      var tileEl = this._resolveTileFromTarget(event.target);
      if (!tileEl) return;
      event.preventDefault();
      var position = this.board.getPositionOfElement(tileEl);
      if (position !== -1 && this.board.isAdjacentToEmpty(position)) {
        this.board.moveTileAtPosition(position);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Cleanup                                                              */
  /* ------------------------------------------------------------------ */

  MDJInput.prototype.destroy = function () {
    if (this.usingPointerEvents) {
      this.boardEl.removeEventListener('pointerdown', this._onPointerDown);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
    } else {
      this.boardEl.removeEventListener('touchstart', this._onTouchStart);
      window.removeEventListener('touchmove', this._onTouchMove);
      window.removeEventListener('touchend', this._onTouchEnd);
      window.removeEventListener('touchcancel', this._onTouchEnd);

      this.boardEl.removeEventListener('mousedown', this._onMouseDown);
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }

    this.boardEl.removeEventListener('keydown', this._onKeyDown);
    this.dragState = null;
  };

  window.MDJRitual.Input = MDJInput;
})();